// Slack API Integration Service

import { QueryClient } from '@tanstack/react-query';

// Slack API Types
export interface SlackChannel {
  id: string;
  name: string;
  is_channel: boolean;
  is_group: boolean;
  is_im: boolean;
  is_mpim: boolean;
  is_private: boolean;
  is_archived: boolean;
  is_general: boolean;
  name_normalized: string;
  is_shared: boolean;
  is_ext_shared: boolean;
  is_org_shared: boolean;
  pending_shared: string[];
  is_pending_ext_shared: boolean;
  is_member: boolean;
  is_open: boolean;
  creator: string;
  created: number;
  updated: number;
  unlinked: number;
  topic: {
    value: string;
    creator: string;
    last_set: number;
  };
  purpose: {
    value: string;
    creator: string;
    last_set: number;
  };
  previous_names: string[];
  num_members?: number;
}

export interface SlackUser {
  id: string;
  team_id: string;
  name: string;
  deleted: boolean;
  color: string;
  real_name: string;
  tz: string;
  tz_label: string;
  tz_offset: number;
  profile: {
    title: string;
    phone: string;
    skype: string;
    real_name: string;
    real_name_normalized: string;
    display_name: string;
    display_name_normalized: string;
    fields: any;
    status_text: string;
    status_emoji: string;
    status_expiration: number;
    avatar_hash: string;
    image_original: string;
    is_custom_image: boolean;
    email: string;
    first_name: string;
    last_name: string;
    image_24: string;
    image_32: string;
    image_48: string;
    image_72: string;
    image_192: string;
    image_512: string;
    image_1024: string;
    status_text_canonical: string;
    team: string;
  };
  is_admin: boolean;
  is_owner: boolean;
  is_primary_owner: boolean;
  is_restricted: boolean;
  is_ultra_restricted: boolean;
  is_bot: boolean;
  is_app_user: boolean;
  updated: number;
  is_email_confirmed: boolean;
  who_can_share_contact_card: string;
}

export interface SlackMessage {
  type: string;
  ts: string;
  user: string;
  text: string;
  team: string;
  user_team: string;
  source_team: string;
  user_profile: {
    avatar_hash: string;
    image_72: string;
    first_name: string;
    real_name: string;
    display_name: string;
    team: string;
    name: string;
    is_restricted: boolean;
    is_ultra_restricted: boolean;
  };
  attachments?: any[];
  blocks?: any[];
  client_msg_id?: string;
  thread_ts?: string;
  reply_count?: number;
  reply_users_count?: number;
  latest_reply?: string;
  reply_users?: string[];
  is_locked?: boolean;
  subscribed?: boolean;
  last_read?: string;
}

export interface SlackApiResponse<T> {
  ok: boolean;
  error?: string;
  warning?: string;
  response_metadata?: {
    next_cursor?: string;
    scopes?: string[];
    acceptedScopes?: string[];
  };
  data?: T;
}

export interface SlackChannelsResponse extends SlackApiResponse<SlackChannel[]> {
  channels: SlackChannel[];
}

export interface SlackUsersResponse extends SlackApiResponse<SlackUser[]> {
  members: SlackUser[];
}

export interface SlackMessagesResponse extends SlackApiResponse<SlackMessage[]> {
  messages: SlackMessage[];
  has_more: boolean;
  pin_count: number;
  response_metadata?: {
    next_cursor?: string;
  };
}

export interface SlackPostMessageResponse extends SlackApiResponse<any> {
  channel: string;
  ts: string;
  message: SlackMessage;
}

// Configuration
const SLACK_BASE_URL = 'http://localhost:3003/api/slack';

class SlackApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errorCode?: string,
    public response?: any
  ) {
    super(message);
    this.name = 'SlackApiError';
  }
}

// Token management for Slack
class SlackTokenManager {
  private static instance: SlackTokenManager;
  private botToken: string | null = null;
  private userToken: string | null = null;

  static getInstance(): SlackTokenManager {
    if (!SlackTokenManager.instance) {
      SlackTokenManager.instance = new SlackTokenManager();
    }
    return SlackTokenManager.instance;
  }

  setBotToken(token: string) {
    this.botToken = token;
  }

  setUserToken(token: string) {
    this.userToken = token;
  }

  getBotToken(): string | null {
    return this.botToken;
  }

  getUserToken(): string | null {
    return this.userToken;
  }
}

// HTTP client for Slack API
class SlackHttpClient {
  private tokenManager = SlackTokenManager.getInstance();

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    useUserToken: boolean = false
  ): Promise<T> {
    const token = useUserToken
      ? this.tokenManager.getUserToken()
      : this.tokenManager.getBotToken();

    if (!token) {
      throw new SlackApiError('No Slack token available', 401);
    }

    const config: RequestInit = {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    console.log('Making Slack API request:', endpoint, 'with token:', token ? token.substring(0, 10) + '...' : 'none');

    const response = await fetch(`${SLACK_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error('Slack API HTTP error:', response.status, error);
      throw new SlackApiError(
        error.error || `HTTP ${response.status}`,
        response.status,
        error.error,
        error
      );
    }

    const data = await response.json();
    console.log('Slack API response:', endpoint, data);

    if (!data.ok) {
      console.error('Slack API logical error:', data.error, data);

      // Provide specific error handling for common issues
      let errorMessage = data.error || 'Slack API Error';
      if (data.error === 'not_in_channel') {
        errorMessage = 'Bot is not in this channel. Please invite the bot to the channel first.';
      } else if (data.error === 'channel_not_found') {
        errorMessage = 'Channel not found. Please check the channel ID.';
      } else if (data.error === 'invalid_auth') {
        errorMessage = 'Invalid Slack authentication token.';
      } else if (data.error === 'missing_scope') {
        errorMessage = 'Bot lacks required permissions. Check OAuth scopes.';
      }

      throw new SlackApiError(
        errorMessage,
        400,
        data.error,
        data
      );
    }

    return data;
  }

  async get<T>(endpoint: string, params?: Record<string, any>, useUserToken?: boolean): Promise<T> {
    const url = params ? `${endpoint}?${new URLSearchParams(params)}` : endpoint;
    return this.request<T>(url, {}, useUserToken);
  }

  async post<T>(endpoint: string, data: any, useUserToken?: boolean): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }, useUserToken);
  }

  async postForm<T>(endpoint: string, data: FormData, useUserToken?: boolean): Promise<T> {
    const token = useUserToken
      ? this.tokenManager.getUserToken()
      : this.tokenManager.getBotToken();

    if (!token) {
      throw new SlackApiError('No Slack token available', 401);
    }

    const config: RequestInit = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: data,
    };

    const response = await fetch(`${SLACK_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new SlackApiError(
        error.error || `HTTP ${response.status}`,
        response.status,
        error.error,
        error
      );
    }

    const result = await response.json();

    if (!result.ok) {
      throw new SlackApiError(
        result.error || 'Slack API Error',
        400,
        result.error,
        result
      );
    }

    return result;
  }
}

// Main Slack API service class
export class SlackApi {
  private client = new SlackHttpClient();

  // Channel operations
  async getChannels(excludeArchived: boolean = true, types: string = 'public_channel,private_channel'): Promise<SlackChannel[]> {
    const params: Record<string, string> = {
      types,
      exclude_archived: excludeArchived.toString(),
      limit: '1000',
    };

    const response = await this.client.get<SlackChannelsResponse>('/conversations.list', params);
    return response.channels || [];
  }

  async getPublicChannels(): Promise<SlackChannel[]> {
    return this.getChannels(true, 'public_channel');
  }

  async getPrivateChannels(): Promise<SlackChannel[]> {
    return this.getChannels(true, 'private_channel');
  }

  async getAllChannels(): Promise<SlackChannel[]> {
    return this.getChannels(true, 'public_channel,private_channel');
  }

  async getChannelInfo(channelId: string): Promise<SlackChannel> {
    const response = await this.client.get<{ ok: boolean; channel: SlackChannel }>('/conversations.info', {
      channel: channelId,
    });
    return response.channel;
  }

  async joinChannel(channelId: string): Promise<boolean> {
    // Try with user token first (has broader permissions), fallback to bot token
    const tokenManager = SlackTokenManager.getInstance();
    const hasUserToken = !!tokenManager.getUserToken();

    const response = await this.client.post<SlackApiResponse<any>>('/conversations.join', {
      channel: channelId,
    }, hasUserToken); // Use user token if available
    return response.ok;
  }

  async leaveChannel(channelId: string): Promise<boolean> {
    const response = await this.client.post<SlackApiResponse<any>>('/conversations.leave', {
      channel: channelId,
    });
    return response.ok;
  }

  // Message operations
  async getChannelMessages(
    channelId: string,
    limit: number = 100,
    cursor?: string
  ): Promise<SlackMessagesResponse> {
    const params: Record<string, string> = {
      channel: channelId,
      limit: limit.toString(),
    };

    if (cursor) {
      params.cursor = cursor;
    }

    return this.client.get<SlackMessagesResponse>('/conversations.history', params);
  }

  async postMessage(
    channelId: string,
    text: string,
    options?: {
      threadTs?: string;
      blocks?: any[];
      attachments?: any[];
      username?: string;
      iconUrl?: string;
      iconEmoji?: string;
    }
  ): Promise<SlackPostMessageResponse> {
    const payload: any = {
      channel: channelId,
      text,
    };

    if (options?.threadTs) payload.thread_ts = options.threadTs;
    if (options?.blocks) payload.blocks = options.blocks;
    if (options?.attachments) payload.attachments = options.attachments;
    if (options?.username) payload.username = options.username;
    if (options?.iconUrl) payload.icon_url = options.iconUrl;
    if (options?.iconEmoji) payload.icon_emoji = options.iconEmoji;

    return this.client.post<SlackPostMessageResponse>('/chat.postMessage', payload);
  }

  async updateMessage(
    channelId: string,
    ts: string,
    text: string,
    options?: {
      blocks?: any[];
      attachments?: any[];
    }
  ): Promise<SlackApiResponse<any>> {
    const payload: any = {
      channel: channelId,
      ts,
      text,
    };

    if (options?.blocks) payload.blocks = options.blocks;
    if (options?.attachments) payload.attachments = options.attachments;

    return this.client.post<SlackApiResponse<any>>('/chat.update', payload);
  }

  async deleteMessage(channelId: string, ts: string): Promise<SlackApiResponse<any>> {
    return this.client.post<SlackApiResponse<any>>('/chat.delete', {
      channel: channelId,
      ts,
    });
  }

  // User operations
  async getUsers(): Promise<SlackUser[]> {
    const response = await this.client.get<SlackUsersResponse>('/users.list');
    return response.members || [];
  }

  async getUserInfo(userId: string): Promise<SlackUser> {
    const response = await this.client.get<{ ok: boolean; user: SlackUser }>('/users.info', {
      user: userId,
    });
    return response.user;
  }

  // Bot operations
  async testConnection(): Promise<{ ok: boolean; url: string; team: string; user: string; team_id: string; user_id: string }> {
    return this.client.get('/auth.test');
  }

  // File operations
  async uploadFile(
    channels: string[],
    file: File,
    options?: {
      title?: string;
      filename?: string;
      filetype?: string;
      initialComment?: string;
      threadTs?: string;
    }
  ): Promise<SlackApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('channels', channels.join(','));

    if (options?.title) formData.append('title', options.title);
    if (options?.filename) formData.append('filename', options.filename);
    if (options?.filetype) formData.append('filetype', options.filetype);
    if (options?.initialComment) formData.append('initial_comment', options.initialComment);
    if (options?.threadTs) formData.append('thread_ts', options.threadTs);

    return this.client.postForm<SlackApiResponse<any>>('/files.upload', formData);
  }

  // Webhook support for incoming messages
  async sendWebhookMessage(webhookUrl: string, payload: any): Promise<boolean> {
    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      return response.ok;
    } catch (error) {
      console.error('Webhook message failed:', error);
      return false;
    }
  }

  // Auth operations
  auth = {
    test: async (): Promise<SlackApiResponse<any>> => {
      return this.client.get<SlackApiResponse<any>>('/auth.test');
    }
  };
}

// Singleton instance
export const slackApi = new SlackApi();

// Token setup helper
export const setupSlackTokens = (botToken: string, userToken?: string) => {
  const tokenManager = SlackTokenManager.getInstance();
  tokenManager.setBotToken(botToken);
  if (userToken) {
    tokenManager.setUserToken(userToken);
  }
};

// Test connection function
export const testSlackConnection = async (): Promise<{ success: boolean; error?: string; data?: any }> => {
  try {
    console.log('Testing Slack connection...');
    const result = await slackApi.auth.test();
    console.log('Slack connection test successful:', result);
    return { success: true, data: result };
  } catch (error) {
    console.error('Slack connection test failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// React Query integration
export const slackQueries = {
  channels: () => ({
    queryKey: ['slack', 'channels'],
    queryFn: () => slackApi.getAllChannels(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  }),

  publicChannels: () => ({
    queryKey: ['slack', 'channels', 'public'],
    queryFn: () => slackApi.getPublicChannels(),
    staleTime: 10 * 60 * 1000,
  }),

  privateChannels: () => ({
    queryKey: ['slack', 'channels', 'private'],
    queryFn: () => slackApi.getPrivateChannels(),
    staleTime: 10 * 60 * 1000,
  }),

  channelInfo: (channelId: string) => ({
    queryKey: ['slack', 'channel', channelId],
    queryFn: () => slackApi.getChannelInfo(channelId),
    staleTime: 30 * 60 * 1000, // 30 minutes
  }),

  channelMessages: (channelId: string, limit?: number) => ({
    queryKey: ['slack', 'messages', channelId, limit],
    queryFn: () => slackApi.getChannelMessages(channelId, limit),
    staleTime: 1 * 60 * 1000, // 1 minute
  }),

  users: () => ({
    queryKey: ['slack', 'users'],
    queryFn: () => slackApi.getUsers(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  }),

  connection: () => ({
    queryKey: ['slack', 'connection'],
    queryFn: () => slackApi.testConnection(),
    staleTime: 60 * 60 * 1000, // 1 hour
  }),
};